import AppContext from 'App/AppContext';
import { IPDefaultButton, IPSeparator, IPTooltipHost } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import GenericModal from 'components/Dialogs/GenericModal';
import FormStructureConflictModal from 'components/Tasks/Forms/FormStructureConflictModal';
import TaskTypeFormEditor from 'components/Tasks/Forms/TaskTypeFormEditor';
import {
  buildFormDiscardCommand,
  prepareDesignTimeFormElements,
} from 'components/Tasks/Forms/taskTypeFormEditor.helpers';
import { useFormStructureAutosave } from 'components/Tasks/Forms/useFormStructureAutosave';
import { globalStackStylesHeight100, globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import KPI from 'models/kpi/kpi';
import TaskType from 'models/tasks/taskType';
import TaskTypeFormElement from 'models/tasks/taskTypeFormElement';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetKPIsAndFields } from 'services/Api/kpiService';
import { apiGetTaskTypeForm } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserDataPermission } from 'services/Auth/featurePermissions';

interface ITaskTypeFormModalProps {
  taskType: TaskType;
  isOpen: boolean;
  onClose: () => void;
  // The names of the entities whose criteria are projected onto this form (procedure hosts supply
  // them): with them the editor shows the fields entity by entity and anchors the criteria
  // commands on those headings.
  entityNamesByKey?: Map<string, string>;
  // Raised with the accepted form whenever a change forks the family into a new version: the host
  // opened this editor on a form its task is pinned to, and the server has moved that pin along.
  onFormVersionChanged?: (taskType: TaskType) => void;
}

const TaskTypeFormModal = (props: ITaskTypeFormModalProps) => {
  const { t } = useTranslation(['translation', 'forms', 'kpis']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formElements, setFormElements] = useState<TaskTypeFormElement[]>([]);
  const [compact, setCompact] = useState<boolean>(true);
  const [kpis, setKPIs] = useState<KPI[]>([]);
  const [currentTaskType, setCurrentTaskType] = useState<TaskType>(props.taskType);
  const [showDiscardChanges, setShowDiscardChanges] = useState<boolean>(false);
  const [showCloseUnsaved, setShowCloseUnsaved] = useState<boolean>(false);
  const [showStructureConflict, setShowStructureConflict] = useState<boolean>(false);

  // A change to a form that is already in use does not amend it: it forks the family into a new
  // version with an id of its own. Every accepted form therefore lands here, because the editor is
  // not the only one holding it - the catalogue the form picker, the tab name and the form a template
  // hands a new task are resolved through has to follow the family, and the host's task has been
  // repointed server-side, so without telling it its next form read asks for a version it is no
  // longer pinned to ("The requested form is not the form pinned to this task").
  const adoptTaskType = (taskType: TaskType) => {
    appContext.globalDataCache.taskTypes.update(taskType);
    setCurrentTaskType(taskType);
    props.onFormVersionChanged?.(taskType);
  };

  const formAutosave = useFormStructureAutosave({
    taskType: currentTaskType,
    // The accepted elements ARE the new form, so they are applied rather than read back: a refetch
    // here blanked the editor for a spinner a second after every reorder.
    onAccepted: (result, elements) => {
      adoptTaskType(result.taskType);
      setFormElements(prepareDesignTimeFormElements(elements));
    },
    onError: (error) => appContext.setError(error),
  });

  useEffect(() => {
    if (formAutosave.failure === 'conflict') {
      setShowStructureConflict(true);
    }
  }, [formAutosave.failure]);

  useEffect(() => {
    if (props.isOpen) {
      setCurrentTaskType(props.taskType);
      void loadData(props.taskType.taskTypeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, props.taskType.taskTypeId]);

  // Latest-wins instead of an isLoading gate: the gate silently DROPPED a reload that raced another
  // one (a criteria write or definition commit finishing while the form still loads), leaving stale
  // pins on screen until the modal was reopened.
  const loadRequestRef = useRef(0);

  const loadData = async (taskTypeId = currentTaskType.taskTypeId) => {
    const requestId = ++loadRequestRef.current;
    try {
      setIsLoading(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const elements = await apiGetTaskTypeForm(taskTypeId, accessToken);
      if (requestId !== loadRequestRef.current) {
        return;
      }

      setFormElements(prepareDesignTimeFormElements(elements));

      const allKpis = await apiGetKPIsAndFields(accessToken, appContext.globalDataCache);
      if (requestId !== loadRequestRef.current) {
        return;
      }

      setKPIs(allKpis);
    } catch (err) {
      appContext.setError(err);
    } finally {
      if (requestId === loadRequestRef.current) {
        setIsLoading(false);
      }
    }
  };

  //closing with edits the server did not accept (a conflict the user kept editing through, a save that
  //keeps failing) is the user's call: the dialog names the loss instead of a silent close
  const close = async () => {
    const saved = await formAutosave.flush();
    if (saved) {
      props.onClose();
    } else {
      setShowCloseUnsaved(true);
    }
  };

  // The discard response is the raw server projection WITHOUT the KPI graph, so applying it leaves
  // every KPI question rendering empty (SingleFormElement needs element.kpi). Read the form back to
  // restore the full elements; the head keeps its id on a discard, it only rewinds its content.
  const discardSessionChanges = async () => {
    setShowDiscardChanges(false);
    const discarded = await formAutosave.discardSession();
    if (discarded) {
      await loadData(formAutosave.getCurrentTaskType()?.taskTypeId ?? currentTaskType.taskTypeId);
    }
  };

  // Recovery and the view toggle only: Discard is a command on the form and lives on the form's own
  // toolbar. On mobile there is no room in the header and nothing left to put there.
  const getOptionalButtons = (): JSX.Element | undefined => {
    if (appContext.isMobileView) return undefined;

    return (
      <IPStack horizontal tokens={globalStackTokensGapSmall}>
        {formAutosave.failure === 'transport' && (
          <IPDefaultButton
            text={t('forms:Versioning.Autosave.Retry')}
            iconProps={{ iconName: 'Refresh' }}
            onClick={() => void formAutosave.retry()}
          />
        )}
        {formAutosave.failure === 'conflict' && (
          <IPDefaultButton
            text={t('forms:Versioning.Conflict.Resolve')}
            iconProps={{ iconName: 'Compare' }}
            onClick={() => setShowStructureConflict(true)}
          />
        )}
        <IPTooltipHost content={t('kpis:Dialogs.Preview.FullscreenTooltip')}>
          <IPDefaultButton
            text={t('kpis:Dialogs.Preview.FullscreenButton')}
            iconProps={{ iconName: 'FullScreen' }}
            checked={!compact}
            onClick={() => {
              setCompact(!compact);
            }}
          />
        </IPTooltipHost>
      </IPStack>
    );
  };

  // An archived form is read-only wherever it can be opened: its structure is frozen and its preview
  // must not invite answers, exactly as the forms administration shows it.
  const editable =
    !currentTaskType.isArchived &&
    hasUserDataPermission(appContext, [props.taskType.authSchemaId], AuthSchemaLineOperation.Update);

  return (
    <>
      <GenericModal
        title={t('forms:Dialogs.EditForm.Title', { name: currentTaskType.name })}
        isOpen={props.isOpen}
        onClose={() => void close()}
        minWidth={300}
        minHeight={600}
        height={'80vh'}
        width={compact ? 600 : 1200}
        maxWidth={'95vw'}
        verticalGap={globalStackTokensGapSmall}
        optionalButtons={getOptionalButtons()}
        transition={'all 0.3s ease'}
        allowFocusOutsideTrap={true}
      >
        <IPStack verticalFill>
          <IPSeparator styles={{ root: { height: 1 } }} />
          <IPStack.Item grow>
            <IPStack horizontal tokens={globalStackTokensGapMedium} styles={globalStackStylesHeight100}>
              <IPStack.Item grow>
                <TaskTypeFormEditor
                  taskTypeId={currentTaskType.taskTypeId}
                  formTaskType={currentTaskType}
                  getCurrentFormTaskType={formAutosave.getCurrentTaskType}
                  kpis={kpis}
                  onKPIsChange={setKPIs}
                  formElements={formElements}
                  editable={editable}
                  readonly={!editable}
                  isSystemForm={!!currentTaskType.systemTaskType}
                  isLoading={isLoading}
                  compact={compact}
                  hideEmptyFormText={true}
                  designTime={true}
                  showFieldCards={true}
                  fieldPanelAsPanel={false}
                  formFieldModalAsPanel={false}
                  toolbarCommands={[
                    buildFormDiscardCommand(t, formAutosave.canDiscard, () => setShowDiscardChanges(true)),
                  ]}
                  onElementsChange={(next, { persist }) => {
                    setFormElements(next);
                    if (persist) {
                      formAutosave.enqueue(next);
                    }
                  }}
                  onStructureCommitted={(taskType, elements) => {
                    formAutosave.rebase(taskType);
                    adoptTaskType(taskType);
                    setFormElements(elements);
                    void loadData(taskType.taskTypeId);
                  }}
                  onBeforeDefinitionCommit={formAutosave.flush}
                  onDefinitionCommitted={(result) => {
                    const current = result.updatedTaskTypes.find(
                      (taskType) => taskType.taskTypeFamilyId === currentTaskType.taskTypeFamilyId,
                    );
                    if (current) {
                      formAutosave.rebase(current);
                      adoptTaskType(current);
                      void loadData(current.taskTypeId);
                    }
                  }}
                  entityNamesByKey={props.entityNamesByKey}
                  // criteria writes re-project the form server-side; re-reading is what makes the
                  // fork/retire/new order visible here
                  onCriteriaChanged={() => void loadData(currentTaskType.taskTypeId)}
                />
              </IPStack.Item>
            </IPStack>
          </IPStack.Item>
        </IPStack>
      </GenericModal>
      {showDiscardChanges && (
        <DialogYesNo
          hidden={!showDiscardChanges}
          title={t('forms:Versioning.Autosave.DiscardTitle')}
          subText={t('forms:Versioning.Autosave.DiscardText')}
          yesText={t('translation:General.Button.Discard')}
          noText={t('translation:General.Button.Cancel')}
          onNo={() => setShowDiscardChanges(false)}
          onYes={() => void discardSessionChanges()}
        />
      )}
      {showCloseUnsaved && (
        <DialogYesNo
          hidden={!showCloseUnsaved}
          title={t('forms:Versioning.Autosave.CloseUnsavedTitle')}
          subText={t('forms:Versioning.Autosave.CloseUnsavedText')}
          yesText={t('translation:General.Button.Discard')}
          noText={t('translation:General.Button.Cancel')}
          onNo={() => setShowCloseUnsaved(false)}
          onYes={() => {
            setShowCloseUnsaved(false);
            props.onClose();
          }}
        />
      )}
      {showStructureConflict && currentTaskType.taskTypeFamilyId && (
        <FormStructureConflictModal
          taskTypeFamilyId={currentTaskType.taskTypeFamilyId}
          localFormElements={formElements}
          isOpen={showStructureConflict}
          onDismiss={() => setShowStructureConflict(false)}
          onReload={(taskType, elements) => {
            formAutosave.rebase(taskType);
            adoptTaskType(taskType);
            setFormElements(elements);
          }}
          onReapply={(taskType, elements) => formAutosave.reapply(taskType, elements)}
        />
      )}
    </>
  );
};

export default TaskTypeFormModal;
